import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Database,
  Settings as SettingsIcon,
  Cannabis,
  FileText,
  Map,
  Bus,
  Users as UsersIcon,
  LogOut,
  CircleUser,
  Star,
  ClipboardList,
  Handshake,
} from "lucide-react";
import { BootstrapPill } from "./BootstrapPill";
import { ThemeToggle } from "./ThemeToggle";
import { fetchBusTastingStats } from "../lib/api";
import { useAuth } from "../lib/AuthProvider";

// Each item declares which roles can see it in the sidebar.
const NAV = [
  { to: "/dashboard",    label: "Dashboard",      icon: LayoutDashboard, testid: "nav-dashboard",     roles: ["admin"] },
  { to: "/dispensaries", label: "Dispensaries",   icon: Database,        testid: "nav-dispensaries",  roles: ["admin", "dispatcher", "sales"] },
  { to: "/map",          label: "Map",            icon: Map,             testid: "nav-map",           roles: ["admin", "dispatcher"] },
  { to: "/bus-tastings", label: "Bus Events",   icon: Bus,             testid: "nav-bus-tastings",  roles: ["admin", "dispatcher"], badge: "bus_tastings_requested" },
  { to: "/my-tastings",  label: "My Events",    icon: Star,            testid: "nav-my-tastings",   roles: ["admin", "dispatcher", "sales", "lead_host", "host", "driver"] },
  { to: "/partners",     label: "Partners",       icon: Handshake,       testid: "nav-partners",      roles: ["admin", "dispatcher", "sales", "lead_host", "host"] },
  { to: "/recap",        label: "Weekly Recap",   icon: ClipboardList,   testid: "nav-recap",         roles: ["admin"] },
  { to: "/admin/users",  label: "Users & Roles",  icon: UsersIcon,       testid: "nav-admin-users",   roles: ["admin"] },
  { to: "/settings",     label: "Settings",       icon: SettingsIcon,    testid: "nav-settings",      roles: ["admin"] },
];

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;
  const initials = (user.name || user.email).slice(0, 2).toUpperCase();
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        data-testid="user-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 transition-colors"
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            className="w-6 h-6 rounded-full"
          />
        ) : (
          <div className="w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-[10px] font-mono text-emerald-300">
            {initials}
          </div>
        )}
        <span className="text-xs font-medium text-zinc-200 max-w-[140px] truncate">
          {user.name || user.email.split("@")[0]}
        </span>
        <span className="text-[9px] uppercase font-mono px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
          {user.role.replace("_", " ")}
        </span>
      </button>
      {open && (
        <div
          data-testid="user-menu"
          className="absolute right-0 top-[calc(100%+6px)] w-56 bg-zinc-950 border border-zinc-800 rounded-md shadow-xl py-1.5 z-30"
        >
          <div className="px-3 py-2 border-b border-zinc-800">
            <div className="text-sm text-zinc-200 font-medium truncate">
              {user.name || user.email}
            </div>
            <div className="text-[10px] text-zinc-500 font-mono truncate">
              {user.email}
            </div>
          </div>
          <button
            type="button"
            data-testid="logout-btn"
            onClick={logout}
            className="w-full text-left px-3 py-2 text-sm text-rose-300 hover:bg-rose-500/10 inline-flex items-center gap-2"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Sidebar() {
  const { user } = useAuth();
  const [tastingStats, setTastingStats] = useState(null);
  const location = useLocation();

  // Only admin/dispatcher have access to /api/bus-tastings/stats — sales
  // and hosts would get a 403 in the console on every page load.
  const canSeeTastingBadge = user?.role === "admin" || user?.role === "dispatcher";

  const refresh = async () => {
    if (!canSeeTastingBadge) return;
    try {
      const s = await fetchBusTastingStats();
      setTastingStats(s);
    } catch {
      /* ignore */
    }
  };

  // Refresh on mount, on route change, and whenever the tab regains focus.
  useEffect(() => {
    refresh();
  }, [location.pathname]);
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const visibleNav = NAV.filter((item) => item.roles.includes(user?.role));

  return (
    <aside
      data-testid="sidebar"
      className="w-60 shrink-0 border-r border-zinc-800 bg-zinc-950 flex flex-col"
    >
      <div className="px-5 py-5 flex items-center gap-2.5 border-b border-zinc-800">
        <div className="w-8 h-8 rounded-md bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
          <Cannabis className="w-4 h-4 text-emerald-400" />
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold text-zinc-50">JackTrack</div>
        </div>
      </div>

      <nav className="flex-1 px-2 py-4 space-y-0.5">
        {visibleNav.map(({ to, label, icon: Icon, testid, badge }) => {
          const badgeCount =
            badge === "bus_tastings_requested"
              ? tastingStats?.requested || 0
              : 0;
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `group flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-zinc-900 text-zinc-50 border border-zinc-800"
                    : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60 border border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span className="font-medium flex-1">{label}</span>
              {badgeCount > 0 && (
                <span
                  data-testid={`nav-badge-${testid}`}
                  className="text-[10px] font-mono font-semibold bg-rose-500/90 text-rose-50 rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center"
                  title={`${badgeCount} pending bus event request${
                    badgeCount === 1 ? "" : "s"
                  }`}
                >
                  {badgeCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-4 py-3 text-[10px] uppercase tracking-[0.12em] text-zinc-600 border-t border-zinc-800 font-mono">
        v0.5 · phase b.5
      </div>
    </aside>
  );
}

function PageTitle() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/dispensaries/")) return "Dispensary Detail";
  if (pathname.startsWith("/snapshots/")) return "Lead Generator Report";
  switch (pathname) {
    case "/dashboard":
      return "Dashboard";
    case "/dispensaries":
      return "Dispensaries";
    case "/map":
      return "Map";
    case "/bus-tastings":
      return "Bus Events";
    case "/snapshots":
      return "Lead Generator";
    case "/recap":
      return "Weekly Recap";
    case "/partners":
      return "Partners";
    case "/settings":
      return "Settings";
    case "/admin/users":
      return "Users & Roles";
    case "/my-tastings":
      return "My Events";
    default:
      return "JackTrack";
  }
}

export function Layout({ headerRight }) {
  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-50">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0">
        <header
          data-testid="app-header"
          className="sticky top-0 z-20 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800 px-8 py-4 flex items-center justify-between"
        >
          <div className="flex items-baseline gap-3">
            <h1
              data-testid="page-title"
              className="text-lg font-semibold tracking-tight text-zinc-50"
            >
              <PageTitle />
            </h1>
          </div>
          <div data-testid="header-actions" className="flex items-center gap-2">
            <BootstrapPill />
            {headerRight}
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>
        <div className="flex-1 px-8 py-6 overflow-x-hidden">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default Layout;
